/**
 * Design tokens for the control-centre surface.
 *
 * These are measured from the Live Tracker reference rather than invented, because the
 * reference is the agreed standard and "close enough" reads as wrong at a glance. Four
 * decisions in it do most of the work, and they are worth stating so they survive edits:
 *
 *  1. THE PAGE IS NOT WHITE. It is a cool paper gradient, 160deg, three stops. A flat
 *     fill makes white cards disappear into the background; a slight tonal drift gives
 *     every panel an edge without drawing one.
 *  2. PANELS ARE TRANSLUCENT, NOT OPAQUE. rgba(255,255,255,.72) over blur, with a
 *     hairline at 7% ink. That is what makes a stack of cards read as one instrument
 *     rather than as a pile of boxes.
 *  3. TWO TYPEFACES, STRICTLY DIVIDED. Instrument Sans carries prose and headings;
 *     JetBrains Mono carries every number and every label. The mono labels run small,
 *     uppercase and widely tracked (.12-.14em) -- that tracking is the single most
 *     recognisable thing about the reference and the cheapest to get wrong.
 *  4. STATUS COLOUR IS A TRIPLE, NOT A HUE. A 10% tint, a 26% border, and a DARK text
 *     shade (#047857, not #10b981). Mid-tier greens on white fail contrast and look
 *     cheap; the dark shade is what makes a pill look printed rather than highlighted.
 *
 * Exported as plain objects, not only as Tailwind classes, because the map layer draws
 * markers to a canvas and deck.gl takes RGB arrays -- neither can read a class name.
 */
export const CC = {
  /** The cool paper. Use as a background-image, not a background-color. */
  page: 'linear-gradient(160deg,#EFF3F9 0%,#E7EDF6 46%,#E9EEF4 100%)',
  pageFlat: '#E9EEF5',
  card: 'rgba(255,255,255,0.72)',
  cardSolid: '#FFFFFF',
  ink: '#0F172A',
  muted: '#475569',
  /** The reference's label grey. Between slate-400 and slate-500, and neither. */
  faint: '#8794A7',
  hairline: 'rgba(15,23,42,0.07)',
  hairlineStrong: 'rgba(15,23,42,0.14)',
  /** One long, soft, downward shadow. Never a tight dark one -- this is paper, not glass. */
  shadow: '0 10px 30px -18px rgba(15,23,42,0.55)',
  shadowLift: '0 20px 46px -30px rgba(15,23,42,0.5)',

  ok: '#047857',
  warn: '#B45309',
  danger: '#9F1239',
  info: '#0369A1',
  /** The brighter twins, for dots, bars and marker fills where mass is small. */
  okBright: '#10B981',
  warnBright: '#F59E0B',
  dangerBright: '#F43F5E',
  infoBright: '#0EA5E9',
  /** Direction of travel. Sky runs south to Dhule, violet runs north to Dhar. */
  south: '#0EA5E9',
  north: '#8B5CF6',
} as const

export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info'

export const TONE_BG: Record<Tone, string> = {
  neutral: 'bg-white/70',
  ok: 'bg-[rgba(16,185,129,0.10)]',
  warn: 'bg-[rgba(245,158,11,0.10)]',
  danger: 'bg-[rgba(244,63,94,0.10)]',
  info: 'bg-[rgba(14,165,233,0.10)]',
}

export const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-[#0F172A]',
  ok: 'text-[#047857]',
  warn: 'text-[#B45309]',
  danger: 'text-[#9F1239]',
  info: 'text-[#0369A1]',
}

/** 26% is the reference's border weight: visible as an edge, invisible as a colour. */
export const TONE_BORDER: Record<Tone, string> = {
  neutral: 'border-[rgba(15,23,42,0.07)]',
  ok: 'border-[rgba(16,185,129,0.26)]',
  warn: 'border-[rgba(245,158,11,0.26)]',
  danger: 'border-[rgba(244,63,94,0.26)]',
  info: 'border-[rgba(14,165,233,0.26)]',
}

/** Solid fills for dots, legend bars and canvas markers. */
export const TONE_HEX: Record<Tone, string> = {
  neutral: CC.faint,
  ok: CC.okBright,
  warn: CC.warnBright,
  danger: CC.dangerBright,
  info: CC.infoBright,
}

/**
 * The label recipe, in one place.
 *
 * Every eyebrow, column header and axis caption in the reference is this exact
 * combination. Spelling it out at each call site guarantees drift.
 */
export const EYEBROW =
  "font-mono text-[9.5px] font-medium uppercase tracking-[0.14em] text-[#8794A7]"

/** Slightly larger and tighter, for labels that sit inside a control rather than above one. */
export const EYEBROW_TIGHT =
  "font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[#8794A7]"

/** Panel chrome. Translucent, hairlined, softly lifted -- see note 2 above. */
export const PANEL =
  'rounded-xl border border-[rgba(15,23,42,0.07)] bg-white/72 backdrop-blur-[14px] ' +
  'shadow-[0_10px_30px_-18px_rgba(15,23,42,0.55)]'
